
import {COMP_TYPES, CompPresentationConfig} from '../lib/presentationConfigs/compConfig.js';
import {findFeatureOption} from './OverlaysService';

/**
 * Consistent interface for presenting any type of content in the UI
 * (tabs, buttons, labels, dropdowns, etc.)
 */
interface ContentPresentation {
  /**
   * Get the type for the content
   */
  getType(): string;

  /**
   * Get the short name for display in tabs, compact spaces
   */
  getShortName(): string;

  /**
   * Get the full display name for labels, headers
   */
  getDisplayName(): string;

  /**
   * Get the pluralized name for lists, counts
   */
  getPluralizedName(): string;

  /**
   * Get the icon name for visual representation
   */
  getIconName(): string;

  /**
   * Get the dropdown/selection label for pickers
   */
  getDropdownLabel(): string;

  /**
   * Get placeholder text for forms/inputs (comp-specific)
   */
  getPlaceholderText?(): string;
}

/**
 * Get the content presentation for a specific component type
 * @param type - The comp type (e.g., 'lease', 'utility')
 * @returns ContentPresentation interface for the given type
 */
function getCompPresentation(type: string): ContentPresentation {
  const data = COMP_TYPES.get(type) as CompPresentationConfig;
  // Safe fallback so callers never crash
  const safe = data || {
    type: type || 'unknown',
    name: 'Item',
    shortName: 'Item',
    pluralizedName: 'Items',
    dropdownLabel: 'Add Item',
    placeholder: 'Enter details…',
    icon: 'circle-help',
  };

  return {
    getType: () => safe.type,
    getShortName: () => safe.shortName,
    getDisplayName: () => safe.name,
    getPluralizedName: () => safe.pluralizedName,
    getIconName: () => safe.icon,
    getDropdownLabel: () => safe.dropdownLabel,
    getPlaceholderText: () => safe.placeholder,
  };
}

/**
 * Get the content presentation for a feature type.
 * Traverses the nested feature options tree (group or leaf) and falls back silently
 * @param featureType - The feature type key (e.g. 'polygon', 'blockPlan-crossDock')
 * @returns ContentPresentation interface for the given feature type
 */
function getFeaturePresentation(featureType: string): ContentPresentation {
  const option = findFeatureOption(featureType);
  return {
    getType: () => featureType,
    getShortName: () => option?.shortName || 'Feature',
    getDisplayName: () => option?.defaultName || featureType,
    getPluralizedName: () => option?.pluralizedName || option?.defaultName || featureType,
    getIconName: () => option?.tabIconName || '',
    getDropdownLabel: () => option?.dropdownLabel || '',
  };
}

interface ContentPresenter {
  handles(type: string): boolean;
  present(type: string): ContentPresentation;
}

const presenters: ContentPresenter[] = [
  {
    handles: (type) => findFeatureOption(type) != null,
    present: (type) => getFeaturePresentation(type),
  },
];

/**
 * Look up the presentation for a content type across registered presenters.
 * @param type - The content type key (e.g. 'label', 'blockPlan-crossDock'), if any
 * @returns The matching ContentPresentation, or null when the type is missing or no presenter has a record for it
 */
function getPresentation(type: string | undefined): ContentPresentation | null {
  if (!type) return null;
  const presenter = presenters.find((p) => p.handles(type));
  return presenter ? presenter.present(type) : null;
}

export {getCompPresentation, getFeaturePresentation, getPresentation};
